import React, { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { PlusCircle, Trash } from '@xsolla/ui-icons';

import { Group } from '../../../../api/users/types';
import { ExtraInputButton, FieldInput, FieldSelect } from '../../../ui';
import { AddGroupButton, MultiInputWrapper } from './style';

type Props = {
  groups: Group[];
  name: string;
  values: number[];
  addInput: () => void;
  deleteInput: (index: number) => void;
  label: ReactNode;
  errors?: Record<string, string>;
  submitFailed: boolean;
};

const SelectUserGroup = ({
  groups,
  name,
  label,
  values,
  addInput,
  deleteInput,
  submitFailed,
  errors
}: Props): JSX.Element => {
  const intl = useIntl();

  const defaultGroup = useMemo(() => groups.find((group) => group.is_default), [groups]);

  const nonDefaultGroupsOptions = useMemo(() => {
    return groups.reduce((acc, { is_default, name: groupName, id }) => {
      if (is_default) {
        return acc;
      }
      return [...acc, { children: groupName, value: id }];
    }, []);
  }, [groups]);

  const getAvailableGroupsOptions = (value: number) => {
    return nonDefaultGroupsOptions.filter(
      (option) => option.value === value || !values.includes(option.value)
    );
  };

  return (
    <>
      <MultiInputWrapper>
        <FieldInput
          label={label}
          readOnly
          extraAlign="right"
          extra={Trash}
          value={defaultGroup?.name}
          name="default_group"
        />
      </MultiInputWrapper>

      {values?.map((value, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <MultiInputWrapper key={`${value}_${index}`}>
            <FieldSelect
              extraRight={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <ExtraInputButton palette="muted" onClick={() => deleteInput(index)}>
                  <Trash />
                </ExtraInputButton>
              }
              options={getAvailableGroupsOptions(value)}
              name={`${name}[${index}]`}
              errorMessage={
                submitFailed
                  ? errors && errors.user_groups && intl.formatMessage({ id: errors.user_groups })
                  : ''
              }
            />
          </MultiInputWrapper>
        );
      })}

      {values.length + 1 < groups.length && (
        <AddGroupButton
          onClick={addInput}
          extraAlign="right"
          extra={PlusCircle}
          defaultValue={intl.formatMessage({ id: 'users.settings.group.select.add' })}
        />
      )}
    </>
  );
};

export default SelectUserGroup;
